/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Custom Product Sticky Form: desktop shows when buy-buttons block is off-screen; pocket also hides when the footer is in view.
//
// main-product.liquid renders this component *before* product-info, so #MainPaymentContainer does not exist on first
// connectedCallback — the base theme would throw; we defer until the buy-buttons node exists or retry budget is hit.
// A passive scroll + rAF sync keeps visibility correct when IntersectionObserver is unreliable (mobile Chrome).
// Pocket: trim a few px off the header inset so the “in view” band matches the real visible strip under the URL bar.
var POCKET_STICKY_TOP_TRIM_PX = 56;
// Pocket: positive bottom root margin / viewport extension so buy buttons rising from below (scroll up) register as
// “in view” before their top crosses the strict layout viewport edge — fixes late sticky hide on mobile Chrome / IO.
var POCKET_STICKY_IO_BOTTOM_EXPAND_PX = 96;

var OBSERVER_SETUP_MAX_FRAMES = 200;

var CustomProductStickyForm = class extends ProductStickyForm {
    connectedCallback() {
        super.connectedCallback();
        this.checkIfStickyFormIsInViewOnLoad();
    }

    disconnectedCallback() {
        if (this._onVisualViewportResize) {
            window.visualViewport?.removeEventListener("resize", this._onVisualViewportResize);
            this._onVisualViewportResize = null;
        }
        if (this._onWindowResizeSync) {
            window.removeEventListener("resize", this._onWindowResizeSync);
            this._onWindowResizeSync = null;
        }
        if (this._onScrollRafSync) {
            window.removeEventListener("scroll", this._onScrollRafSync, { passive: true });
            this._onScrollRafSync = null;
        }
        if (this._scrollRaf != null) {
            cancelAnimationFrame(this._scrollRaf);
            this._scrollRaf = null;
        }
        if (this._footerIntersectionObserver) {
            this._footerIntersectionObserver.disconnect();
            this._footerIntersectionObserver = null;
        }
        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = null;
        }
        // Do not call ProductStickyForm.disconnectedCallback — it does this.intersectionObserver.disconnect() unguarded
        // and throws when setup never attached an observer (DOM order / missing buy buttons).
    }

    /** Buy buttons block (.product-form__buy-buttons); wider than #MainPaymentContainer so gift-card rows still count as "in view". */
    _getBuyButtonsElement() {
        const paymentEl = document.getElementById("MainPaymentContainer");
        if (paymentEl) {
            return paymentEl.closest(".product-form__buy-buttons");
        }
        return document.querySelector(".product-form__buy-buttons");
    }

    _getStickyViewportTopInset() {
        const raw = getStickyHeaderOffset();
        const isPocket = window.matchMedia(window.themeVariables.breakpoints.pocket).matches;
        if (!isPocket) {
            return raw;
        }
        return Math.max(0, raw - POCKET_STICKY_TOP_TRIM_PX);
    }

    _getStickyViewportBottomBound(includePocketBottomExpand) {
        const base = window.innerHeight;
        const expand =
            includePocketBottomExpand &&
            window.matchMedia(window.themeVariables.breakpoints.pocket).matches
                ? POCKET_STICKY_IO_BOTTOM_EXPAND_PX
                : 0;
        return base + expand;
    }

    /**
     * True if rect overlaps the band below the header. On pocket, optional bottom expansion matches IO when buy
     * buttons rise from below the layout viewport edge (disable for footer overlap checks).
     */
    _elementIntersectsStickyViewport(rect, topInset, includePocketBottomExpand = true) {
        const bottomBound = this._getStickyViewportBottomBound(includePocketBottomExpand);
        return rect.bottom > topInset && rect.top < bottomBound;
    }

    checkIfStickyFormIsInViewOnLoad() {
        const run = () => this._syncHiddenFromRects();
        if (document.readyState === "loading") {
            document.addEventListener("DOMContentLoaded", () => setTimeout(run, 0));
        } else {
            setTimeout(run, 0);
        }
    }

    _syncHiddenFromRects() {
        const buyButtonsEl = this._getBuyButtonsElement();
        const footerEl = document.querySelector(".shopify-section--footer");
        const topInset = this._getStickyViewportTopInset();
        if (!buyButtonsEl) {
            return;
        }
        const buyButtonsIntersects = this._elementIntersectsStickyViewport(buyButtonsEl.getBoundingClientRect(), topInset);
        const footerIntersects = footerEl
            ? this._elementIntersectsStickyViewport(footerEl.getBoundingClientRect(), topInset, false)
            : false;
        const isPocket = window.matchMedia(window.themeVariables.breakpoints.pocket).matches;
        this.hidden = isPocket ? buyButtonsIntersects || footerIntersects : buyButtonsIntersects;
    }

    _scheduleScrollSyncRaf() {
        if (this._scrollRaf != null) {
            return;
        }
        this._scrollRaf = requestAnimationFrame(() => {
            this._scrollRaf = null;
            this._syncHiddenFromRects();
        });
    }

    _bindScrollRafSync() {
        if (this._onScrollRafSync) {
            return;
        }
        this._onScrollRafSync = () => this._scheduleScrollSyncRaf();
        window.addEventListener("scroll", this._onScrollRafSync, { passive: true });
    }

    _updateHiddenFromIntersection() {
        const isPocket = window.matchMedia(window.themeVariables.breakpoints.pocket).matches;
        this.hidden = isPocket ? this._buyButtonsIntersects || this._footerIntersects : this._buyButtonsIntersects;
    }

    _setupVisibilityObservers() {
        if (this.intersectionObserver) {
            return;
        }

        const buyButtonsEl = this._getBuyButtonsElement();
        if (!buyButtonsEl) {
            if (this._observerSetupFrames === undefined) {
                this._observerSetupFrames = 0;
            }
            if (this._observerSetupFrames < OBSERVER_SETUP_MAX_FRAMES) {
                this._observerSetupFrames += 1;
                requestAnimationFrame(() => this._setupVisibilityObservers());
            }
            return;
        }
        this._observerSetupFrames = 0;

        const footerEl = document.querySelector(".shopify-section--footer");
        const topInset = this._getStickyViewportTopInset();
        const bottomExpandPx = window.matchMedia(window.themeVariables.breakpoints.pocket).matches
            ? POCKET_STICKY_IO_BOTTOM_EXPAND_PX
            : 0;

        this._buyButtonsIntersects = false;
        this._footerIntersects = false;

        this.intersectionObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.target === buyButtonsEl) {
                    this._buyButtonsIntersects = entry.isIntersecting;
                }
            });
            this._updateHiddenFromIntersection();
        }, {
            // Positive bottom expands the IO root downward: buy buttons scrolling up “from the bottom” intersect sooner.
            rootMargin: `-${topInset}px 0px ${bottomExpandPx}px 0px`
        });
        this.intersectionObserver.observe(buyButtonsEl);

        if (footerEl) {
            this._footerIntersectionObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.target === footerEl) {
                        this._footerIntersects = entry.isIntersecting;
                    }
                });
                this._updateHiddenFromIntersection();
            }, { rootMargin: `-${topInset}px 0px 0px 0px` });
            this._footerIntersectionObserver.observe(footerEl);
        }

        this._onVisualViewportResize = () => this._syncHiddenFromRects();
        window.visualViewport?.addEventListener("resize", this._onVisualViewportResize);
        this._onWindowResizeSync = () => this._syncHiddenFromRects();
        window.addEventListener("resize", this._onWindowResizeSync);

        this._bindScrollRafSync();
        this._syncHiddenFromRects();
    }
};

window.customElements.define("custom-product-sticky-form", CustomProductStickyForm);


class RecommendationsProducts extends HTMLElement {
    constructor() {
        super();
        this.productId = this.getAttribute('product-id');
        this.sectionId = this.getAttribute('section-id');
        this.intent = this.getAttribute('intent') || 'related';
        this.loaded = false;
        this.renderProductsElement = this.querySelector('[render-products]');
    }

    connectedCallback() {
        if (!this.productId || !this.sectionId) {
            // Required attributes missing; don't load
            return;
        }
        if (!this.loaded) {
            this.loadRecommendations();
        }
    }

    async loadRecommendations() {
        try {
            // Shopify recommends this endpoint for product recommendations.
            // /recommendations/products?section_id=[sectionId]&product_id=[productId]&intent=[intent]
            const params = new URLSearchParams({
                section_id: this.sectionId,
                product_id: this.productId,
                intent: this.intent
            });
            const url = window.Shopify.routes.root + `recommendations/products?product_id=${this.productId}&limit=4&section_id=${this.sectionId}&intent=${this.intent}`;

            const response = await fetch(url, { credentials: 'same-origin' });
            if (!response.ok) throw new Error('Network response was not ok');

            const html = await response.text();

            // Render only the children of the first (and only) <div> in the fetched HTML
            const tempContainer = document.createElement('div');
            tempContainer.innerHTML = html;

            let childrenSource;

            // If there's a single child and it's a <div>, use its children. Otherwise, use all children of tempContainer.
            if (
                tempContainer.childElementCount === 1 &&
                tempContainer.firstElementChild.tagName === 'DIV'
            ) {
                childrenSource = tempContainer.firstElementChild;
            } else {
                childrenSource = tempContainer;
            }

            // Clear products element and append new children
            this.renderProductsElement.innerHTML = '';
            while (childrenSource.firstChild) {
                this.renderProductsElement.appendChild(childrenSource.firstChild);
            }
            this.loaded = true;

            // Dispatch a custom event in case something else needs to know
            this.dispatchEvent(new CustomEvent('recommendations:loaded', {
                detail: {
                    productId: this.productId,
                    sectionId: this.sectionId,
                    intent: this.intent
                },
                bubbles: true
            }));

        } catch (e) {
            this.innerHTML = "<div class='recommendations-error'>Unable to load product recommendations.</div>";
            // Optionally emit an error event
            this.dispatchEvent(new CustomEvent('recommendations:error', {
                detail: { error: e },
                bubbles: true
            }));
        }
    }
}

window.customElements.define('recommendations-products', RecommendationsProducts);
